import { addCalendarDays, stationCalendarDay, zoneOfLocation } from './stationTime';

/**
 * "Does this booking start / end today / tomorrow?" (#3016) in the admin UI:
 * the bookings-list filter the dashboard's "Today" / "Tomorrow" figures link
 * to (`?day=today&dayEdge=start`, `makeBookingsOnDayUrl`).
 *
 * MIRROR — `new-backend/admin/src/lib/booking/bookingDay.ts`, which counts the
 * dashboard figure. The two cannot import each other and the figure must
 * equal the list it opens, so both are pinned by one fixture:
 * `shared/booking/booking-day-vectors.json` (`bookingDay.test.ts` here and
 * beside the backend copy). Change one, change the other, add the case to the
 * fixture.
 *
 * The rule: a day is a calendar day at the booking's location (ADR-0019) —
 * both the day the start or end falls on and what "today" is there. A
 * multi-day booking starts on its first day and ends on its last. Cancelled
 * bookings and blockers never count; a booking without the instant (parcel)
 * never counts.
 */
export type DayPreset = 'today' | 'tomorrow';
export type DayEdge = 'start' | 'end';

export const DAY_QUERY_PARAM = 'day';
export const DAY_EDGE_QUERY_PARAM = 'dayEdge';

const DAY_PRESETS: readonly DayPreset[] = ['today', 'tomorrow'];
const DAY_EDGES: readonly DayEdge[] = ['start', 'end'];

/** The list's day filter: which day, and whether the start or the end falls on it. */
export interface DayFilter {
    preset: DayPreset;
    edge: DayEdge;
}

/** The facts the rule reads, resolved from a list row by `bookingDayFactsOf`. */
export interface BookingDayFacts {
    start: string | null;
    end: string | null;
    /** The location's IANA zone, resolved through `zoneOfLocation`. */
    timeZone: string;
    cancelled: boolean;
    blocker: boolean;
}

/** `YYYY-MM-DD` of `preset` at the station, seen from `now`. */
export const dayOfPreset = (preset: DayPreset, now: Date, timeZone: string): string => {
    const today = stationCalendarDay(now, timeZone);
    return preset === 'today' ? today : addCalendarDays(today, 1);
};

const isInstant = (value: string | null): value is string =>
    typeof value === 'string' && value !== '' && !Number.isNaN(new Date(value).getTime());

/** Does the booking start (`edge = 'start'`) or end on `preset`, seen from `now`? */
export const bookingIsOnDay = (facts: BookingDayFacts, edge: DayEdge, preset: DayPreset, now: Date): boolean => {
    if (facts.cancelled || facts.blocker) return false;
    const instant = edge === 'start' ? facts.start : facts.end;
    if (!isInstant(instant)) return false;
    return stationCalendarDay(instant, facts.timeZone) === dayOfPreset(preset, now, facts.timeZone);
};

/** The fields of a bookings-list row the rule reads (`rental/bookings/:useCase`). */
export interface BookingDayRow {
    startDate?: string | null;
    endDate?: string | null;
    bookingStatus?: string | null;
    locationTimeZone?: string | null;
    isCanceled?: boolean | null;
    isFakeForBlocking?: boolean | null;
}

/**
 * A list row's facts. Cancelled is the status the row shows OR the order's own
 * flag; the backend copy reads `Order.is_canceled` and the deactivated rental
 * item — the two facts that status is derived from — so the flag here keeps a
 * cancelled booking out even where the row's status reads otherwise.
 */
export const bookingDayFactsOf = (row: BookingDayRow): BookingDayFacts => {
    const status = (row.bookingStatus ?? '').toLowerCase();
    return {
        start: row.startDate ?? null,
        end: row.endDate ?? null,
        timeZone: zoneOfLocation(row.locationTimeZone),
        cancelled: status === 'canceled' || row.isCanceled === true,
        blocker: status === 'blocked' || row.isFakeForBlocking === true,
    };
};

/** `?day=…&dayEdge=…` as a filter, or null when absent or not one we know. */
export const parseDayFilter = (params: URLSearchParams): DayFilter | null => {
    const preset = params.get(DAY_QUERY_PARAM)?.toLowerCase();
    const edge = params.get(DAY_EDGE_QUERY_PARAM)?.toLowerCase();
    if (!preset || !edge) return null;
    if (!(DAY_PRESETS as readonly string[]).includes(preset)) return null;
    if (!(DAY_EDGES as readonly string[]).includes(edge)) return null;
    return { preset: preset as DayPreset, edge: edge as DayEdge };
};

/** The query string of a day filter, for a link. */
export const dayFilterQuery = (filter: DayFilter): string =>
    `${DAY_QUERY_PARAM}=${filter.preset}&${DAY_EDGE_QUERY_PARAM}=${filter.edge}`;

/** Every filter the list offers, in the order the filter panel lists them. */
export const DAY_FILTER_OPTIONS: readonly DayFilter[] = [
    { preset: 'today', edge: 'start' },
    { preset: 'today', edge: 'end' },
    { preset: 'tomorrow', edge: 'start' },
    { preset: 'tomorrow', edge: 'end' },
];

/** A stable key of a filter, for a radio value. */
export const dayFilterKey = (filter: DayFilter): string => `${filter.edge}_${filter.preset}`;
